"use client";
import { useEffect, useState, useCallback } from "react";
import svgPaths from "@/imports/svg-on5oq4kwhg";
import { SectionTitle } from "@/components/common/SectionTitle";
import { ViewAllButton } from "@/components/common/ViewAllButton";
import { clippingService } from "@/services/clipping.service";

const colors = {
  primary: "#0248fd",
  text: "#010103",
  textSecondary: "#65676e",
  textMuted: "#979ba5",
  textLight: "#6a6b73",
  white: "#ffffff",
};

function SourceLinkIcon() {
  return (
    <div className="relative shrink-0 w-[14px] h-[14px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g>
          <path d={svgPaths.p328a5f80} stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
        </g>
      </svg>
    </div>
  );
}

function ViewSourceButton({ href }: { href?: string }) {
  return (
    <a
      className="inline-flex justify-start items-center gap-1 hover:opacity-70 transition-opacity cursor-pointer whitespace-nowrap min-w-fit"
      href={href || '#'}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="원문보기"
    >
      <div className="justify-start text-black text-base lg:text-base font-medium font-['Pretendard'] leading-tight">
        원문보기
      </div>
      <div className="flex-shrink-0">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 lg:w-5 lg:h-5">
          <path d="M8.33301 3.33594H4.99967C4.55765 3.33594 4.13372 3.51153 3.82116 3.82409C3.5086 4.13665 3.33301 4.56058 3.33301 5.0026V15.0026C3.33301 15.4446 3.5086 15.8686 3.82116 16.1811C4.13372 16.4937 4.55765 16.6693 4.99967 16.6693H14.9997C15.4417 16.6693 15.8656 16.4937 16.1782 16.1811C16.4907 15.8686 16.6663 15.4446 16.6663 15.0026V11.6693M9.99967 10.0026L16.6663 3.33594M16.6663 3.33594V7.5026M16.6663 3.33594H12.4997" stroke="#010103" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </a>
  );
}

interface PublicationDateProps { date: string; linkUrl?: string }
function PublicationDate({ date, linkUrl }: PublicationDateProps) {
  return (
    <div className="self-stretch flex flex-col justify-start items-start gap-5">
      <div className="self-stretch h-7 relative flex justify-between items-center">
        <div className="justify-start text-gray-400 text-base lg:text-lg font-normal font-['Pretendard'] leading-tight">
          {date}
        </div>
        <div className="flex-shrink-0">
          <ViewSourceButton href={linkUrl} />
        </div>
      </div>
    </div>
  );
}

interface ArticleContentProps { content: string; date: string; linkUrl?: string }
function ArticleContent({ content, date, linkUrl }: ArticleContentProps) {
  return (
    <div className="self-stretch flex flex-col justify-start items-start gap-5">
      <div className="self-stretch justify-start text-neutral-500 text-xl lg:text-lg font-medium font-['Pretendard'] leading-relaxed line-clamp-6 overflow-hidden">
        {content}
      </div>
      <PublicationDate date={date} linkUrl={linkUrl} />
    </div>
  );
}

interface ClippingCardProps { title: string; content: string; date: string; linkUrl?: string; gridArea: string; }
function ClippingCard({ title, content, date, linkUrl, gridArea }: ClippingCardProps) {
  return (
    <div className={`${gridArea} w-full h-auto px-4 lg:px-6 lg:px-10 py-6 lg:py-10 lg:py-12 bg-neutral-100 rounded-[20px] inline-flex flex-col justify-start items-start gap-2.5`}>
      <div className="w-full flex flex-col justify-start items-start gap-4 lg:gap-6">
        <div className="self-stretch justify-start text-black text-2xl lg:text-2xl lg:text-3xl font-semibold font-['Pretendard'] leading-tight lg:leading-10 line-clamp-2 overflow-hidden">
          {title}
        </div>
        <ArticleContent content={content} date={date} linkUrl={linkUrl} />
      </div>
    </div>
  );
}

function ThreeColumnClippingList() {
  const [items, setItems] = useState<{ title: string; summary: string; publishedAt?: string; linkUrl?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState<'mobile' | 'desktop'>('desktop');
  
  // 화면 크기 감지
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    if (width < 1024) { // lg breakpoint - 1024px 이하를 모두 모바일로 인식
      setScreenSize('mobile');
    } else {
      setScreenSize('desktop');
    }
  }, []);
  
  // 화면 크기 감지 이벤트 리스너
  useEffect(() => {
    handleResize(); // 초기 설정
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  
  // 화면 크기에 따른 보여줄 카드 수 계산
  const getCardsToShow = () => {
    switch (screenSize) {
      case 'mobile': return 1;
      case 'desktop': return 3;
      default: return 3;
    }
  };

  useEffect(() => {
    setLoading(true);
    clippingService.getWeeklyClipping()
      .then((response) => {
        // To-Be 형식 처리
        // WeeklyClippingResponse는 articles를 반환
        const data = response.code === "SUCCESS" && response.data?.articles 
          ? response.data.articles 
          : [];
        const cardsToShow = getCardsToShow();
        setItems(Array.isArray(data) ? data.slice(0, cardsToShow) : []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [screenSize]); // screenSize가 변경될 때마다 재로드

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-[355px]">
        <div className="size-8 animate-spin rounded-full border-4 border-[#dadbe0] border-t-[#43464d]" aria-label="로딩 중" />
      </div>
    );
  }

  return (
    <div className="box-border gap-4 lg:gap-6 lg:gap-[30px] grid grid-cols-1 lg:grid-cols-2 lg:grid-cols-3 grid-rows-[repeat(1,_minmax(0px,_1fr))] h-auto pb-5 pt-0 px-0 relative shrink-0 w-full">
      {items.map((article, index) => (
        <ClippingCard
          key={index}
          title={article.title}
          content={article.summary || ""}
          date={article.publishedAt ? new Date(article.publishedAt).toLocaleDateString('ko-KR') : ""}
          linkUrl={article.linkUrl}
          gridArea={`[grid-area:1_/_${index+1}]`}
        />
      ))}
    </div>
  );
}

function renderTitleNodesForSection(titleHtml: string) {
  const parts = String(titleHtml).split(/(<blue>[\s\S]*?<\/blue>)/gi).filter(Boolean);
  return parts.map((part, idx) => {
    const m = part.match(/^<blue>([\s\S]*?)<\/blue>$/i);
    if (m) {
      return (
        <div key={idx} className="flex flex-col justify-center relative shrink-0 text-[#0248fd]">
          <p className="font-semibold leading-[normal] text-nowrap whitespace-pre">{m[1]}</p>
        </div>
      );
    }
    return (
      <div key={idx} className="flex flex-col justify-center relative shrink-0 text-[#010103]">
        <p className="font-semibold leading-[normal] text-nowrap whitespace-pre">{part}</p>
      </div>
    );
  });
}

function SectionHeader({ 
  blueTitle, 
  blackTitle, 
  titleHtml 
}: { 
  blueTitle: string; 
  blackTitle: string; 
  titleHtml?: string; 
}) {
  return (
    <div className="gap-2.5 grid grid-cols-[1fr_auto] grid-rows-[repeat(1,_minmax(0px,_1fr))] h-auto lg:h-[45px] overflow-clip relative shrink-0 w-full">
      <div className="content-stretch flex font-['Pretendard:Medium',_sans-serif] items-center justify-start leading-[0] not-italic overflow-clip relative shrink-0 text-xl lg:text-2xl lg:text-[32px] text-nowrap">
        {typeof titleHtml === 'string' && titleHtml.length > 0 ? (
          <>
            {renderTitleNodesForSection(titleHtml)}
          </>
        ) : (
          <>
            <div className="flex flex-col justify-center relative shrink-0" style={{ color: colors.primary }}>
              <p className="font-semibold leading-[normal] text-nowrap whitespace-pre">{blueTitle}</p>
            </div>
            <div className="flex flex-col justify-center relative shrink-0" style={{ color: colors.text }}>
              <p className="font-semibold leading-[normal] text-nowrap whitespace-pre">{blackTitle}</p>
            </div>
          </>
        )}
      </div>
      <div className="content-stretch flex gap-[15px] items-center justify-end relative shrink-0">
        <ViewAllButton href="/clipping" publicAccess />
      </div>
    </div>
  );
}

export function ThreeColumnClippingSection({ 
  blueTitle = "파란색제목", 
  blackTitle = "검정색제목", 
  titleHtml 
}: { 
  blueTitle?: string; 
  blackTitle?: string; 
  titleHtml?: string; 
}) {
  return (
    <div className="content-stretch flex flex-col gap-5 items-center justify-start relative size-full">
      <SectionHeader blueTitle={blueTitle} blackTitle={blackTitle} titleHtml={titleHtml} />
      <ThreeColumnClippingList />
    </div>
  );
}

